import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Button, Flex, ProgressBar, View, Heading, Text, } from '@smarterservices/smarter-ui';
import PropTypes from 'prop-types';
import PaneViewerContext, { usePaneViewer } from './PaneViewerContext';
import DocumentTitle from '@shared/components/Common/DocumentTitle';

export { usePaneViewer };

/**
 * PaneViewer
 *
 * A higher-level layout container that displays:
 *   1. A title at the top.
 *   2. A main content area (no scrolling here).
 *   3. A Next button pinned at the bottom.
 *
 * Scrolling is delegated to SinglePane or DualPane.
 *
 * @param {Object} props - Component props
 * @param {Object} props.step - Step information including title and progress
 * @param {Object} props.step.stepData - Step metadata including title
 * @param {string|ReactNode} props.step.stepData.title - Title to display in the header area
 * @param {number} props.step.remaining - Number of remaining steps (for progress bar)
 * @param {number} props.step.total - Total number of steps (for progress bar)
 * @param {boolean} props.nextEnabled - Whether the next button should be enabled
 * @param {Function} [props.onNext] - Callback for the Next button. If null, no next button will appear
 * @param {boolean} [props.inSession=true] - Whether it is an in-session reconnect (hides progress bar)
 * @param {boolean} [props.showNext=false] - Whether to show the Next button (when onNext is provided)
 * @param {ReactNode} props.children - The content to display in the main area
 */
const PaneViewer = ({
  step,
  phase,
  nextEnabled,
  onNext = null,
  inSession = true,
  isLoading = false,
  setIsLoading = () => {},
  showNext: initialShowNext = false,
  setStepMetadata = () => {},
  setIsTaskComplete = () => {},
  children,
}) => {
  const [showNext, setShowNext] = useState(initialShowNext);
  const [nextButtonText, setNextButtonText] = useState(step?.nextButtonText || '');
  const [showProgressDetail, setShowProgressDetail] = useState(false);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [borderRadius, setBorderRadius] = useState("medium medium none none");
  const [hideTitle, setHideTitle] = useState(false);
  const borderColor = 'primary';
  const backgroundColor = 'primary';
    
  // Reset state when step changes
  useEffect(() => {
    setShowNext(initialShowNext || !!step?.nextButtonText);
    if (step?.nextButtonText) {
      setNextButtonText(step.nextButtonText);
    }

    if (phase?.total && phase?.remaining !== undefined) {
      const stepsCompleted = phase.total - phase.remaining;
      const completionPercentage = Math.min((stepsCompleted / phase.total) * 100, 100);
      setCompletionPercentage(completionPercentage);
      
      if (completionPercentage === 100) {
        setBorderRadius("none");
      } else {
        setBorderRadius("none medium medium none");
      }
    } else {
      setCompletionPercentage(0);
      setBorderRadius("none");
    }

  }, [step, initialShowNext, phase]);

  // Wrap onNext in useCallback to prevent unnecessary re-renders
  const handleNext = useCallback(() => {
    if (onNext) onNext();
  }, [onNext]);
  
  // Create the context value
  const contextValue = useMemo(() => ({
    step,
    phase,
    nextEnabled,
    onNext: handleNext,
    inSession,
    showNext,
    setShowNext,
    isLoading,
    setIsLoading,
    setStepMetadata,
    nextButtonText,
    setNextButtonText: (text) => {
      setNextButtonText(text);
      setShowNext(!!text);
    },
    hideTitle,
    setHideTitle,
    setIsTaskComplete
  }), [step, phase, nextButtonText, nextEnabled, handleNext, inSession, showNext, setShowNext, setStepMetadata, hideTitle, setHideTitle, setIsTaskComplete]);
  return (
    <PaneViewerContext.Provider value={contextValue}>
      <DocumentTitle value={`Proctoring - ${step?.stepData?.title || 'Onboarding'}`} />
      <Flex
        as="div"
        justifyItems="center"
        padding="small"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <View
          as="div"
          display="block"
          borderWidth="small"
          borderColor={borderColor}
          margin="none"
          width="100%"
          height="100%"
          background={backgroundColor}
          className="pane-viewer"
        >
          <Flex direction="column" height="100%" width="100%">
            {/* Progress Bar */}
            {(!inSession && completionPercentage > 0) && (
              
              <View as="div" 
                background='info'
                height={showProgressDetail ? "25px" : "8px"}
                textAlign='center'
                borderRadius={borderRadius}
                width={`${completionPercentage}%`}
                onMouseOver={
                  ()=>{
                    setShowProgressDetail(true);
                  }
                }
                onMouseOut={
                  ()=>{
                    setShowProgressDetail(false);
                  }
                }
              >
                {(showProgressDetail && completionPercentage < 100) && (
                  <Text size='x-small'>Step {phase?.total - phase?.remaining} of {phase?.total}</Text>
                )}  
                {(showProgressDetail && completionPercentage === 100) && (
                  <Text size='x-small'>All steps completed!</Text>
                )}             
              </View>
            )}
            {/* Title */}
            {(step?.stepData?.title && !hideTitle && !isLoading) && (
              <View
                as="div"
                display="block"
                borderWidth="0 0 small 0"
                borderColor={borderColor}
                borderRadius='medium medium none none'
                padding="small"
                background={backgroundColor}
                textAlign="center"
                className="pane-viewer-title"
              >
                <Heading 
                  level="h1" 
                  themeOverride={{
                    weightImportant: 500,
                    h1FontSize: '1.7rem'
                  }}
                >{step.stepData.title}</Heading>
              </View>
            )}

            {/* Main Content */}
            <Flex.Item shouldGrow shouldShrink className="pane-viewer-content">
              {children}
            </Flex.Item>

            {/* Next Button */}
            {(onNext && showNext) && (
              <View
                display="block"
                borderWidth="small 0 0 0"
                borderColor={borderColor}
                padding="small"
                className="pane-viewer-next-button"
              >
                <Flex direction="row" width="100%" justifyItems="end">
                  <Button
                    interaction={nextEnabled ? 'enabled' : 'disabled'}
                    onClick={handleNext}
                    color="primary"
                  >
                    {nextButtonText || 'Next'}
                  </Button>
                </Flex>
              </View>
            )}
          </Flex>
        </View>
      </Flex>
    </PaneViewerContext.Provider>
  );
};

PaneViewer.propTypes = {
  step: PropTypes.object.isRequired,
  nextEnabled: PropTypes.bool.isRequired,
  onNext: PropTypes.func,
  setIsTaskComplete: PropTypes.func,
  setStepMetadata: PropTypes.func,
  inSession: PropTypes.bool,
  showNext: PropTypes.bool,
  isLoading: PropTypes.bool,
  children: PropTypes.node,
};

export default PaneViewer;
